import { mat4, vec3 } from 'gl-matrix';

import { Camera } from './Camera';
import { halton } from '../math';
import { ViewFrustum } from '../util';

/** Squared distance threshold for camera-teleport detection (10m). */
const TELEPORT_THRESHOLD_SQR = 100;

export abstract class DefaultCamera extends Camera {
  protected invalidatedModelView: boolean;
  protected invalidatedProjection: boolean;

  protected _invertedViewMatrix: mat4;
  protected _invProjection: mat4;
  protected _modelViewMatrix: mat4;
  protected _projectionMatrix: mat4;
  protected _viewFrustum: ViewFrustum;

  protected _unjitteredProjection: mat4;
  protected _unjitteredViewProjection: mat4;
  protected _previousViewProjection: mat4;
  protected _haltonIndex = 0;
  protected _jitterOffset: [number, number] = [0, 0];

  public override get taaFrameIndex(): number {
    return this.taaJitterEnabled ? this._haltonIndex + 1 : 0;
  }

  private readonly _lastCameraPosition: vec3 = vec3.create();
  private _lastCameraPositionValid = false;

  public get modelViewMatrix(): mat4 {
    if (this.invalidatedModelView) {
      this.applyModelView(this._modelViewMatrix);
      this.updateInvertedMatrix();
      this._viewFrustum.refresh();
      this.invalidatedModelView = false;
    }
    return this._modelViewMatrix;
  }

  public get projectionMatrix(): mat4 {
    if (this.invalidatedProjection) {
      this.applyProjection(this._projectionMatrix);
      this.updateInvertedMatrix();
      this._viewFrustum.refresh();
      this.invalidatedProjection = false;
    }
    return this._projectionMatrix;
  }

  constructor(dimensions: { width: number; height: number }) {
    super(dimensions);
    this._modelViewMatrix = mat4.identity(mat4.create());
    this._projectionMatrix = mat4.create();
    this._invertedViewMatrix = mat4.create();
    this._invProjection = mat4.create();
    this._unjitteredProjection = mat4.create();
    this._unjitteredViewProjection = mat4.create();
    this._previousViewProjection = mat4.create();

    this.invalidatedModelView = true;
    this.invalidatedProjection = true;

    this._viewFrustum = new ViewFrustum(this._invertedViewMatrix);
  }

  protected updateInvertedMatrix(): void {
    mat4.multiply(this._invertedViewMatrix, this._projectionMatrix, this._modelViewMatrix);
    mat4.invert(this._invertedViewMatrix, this._invertedViewMatrix);
    mat4.invert(this._invProjection, this._projectionMatrix);
    mat4.multiply(
      this._unjitteredViewProjection,
      this._unjitteredProjection,
      this._modelViewMatrix,
    );
  }

  // Inverse of (projection * view). Used by deferred passes to reconstruct
  // world position from a depth sample + screen UV (replaces an explicit
  // RGBA32F position attachment in the G-buffer).
  public override get invViewProjection(): mat4 {
    // Touch the model-view / projection getters so the inverted matrix is up to date.
    this.modelViewMatrix;
    this.projectionMatrix;
    return this._invertedViewMatrix;
  }

  // Inverse of the projection matrix only. Used by SSAO to reconstruct view-
  // space position from depth without going through world space.
  public override get invProjection(): mat4 {
    this.projectionMatrix;
    return this._invProjection;
  }

  public override get unjitteredProjection(): mat4 {
    this.projectionMatrix;
    return this._unjitteredProjection;
  }

  public override get unjitteredViewProjection(): mat4 {
    this.modelViewMatrix;
    this.projectionMatrix;
    return this._unjitteredViewProjection;
  }

  public override get previousViewProjection(): mat4 {
    return this._previousViewProjection;
  }

  protected applyProjection(matrix: mat4): void {
    this.applyPerspective(this._unjitteredProjection, this.fov);
    mat4.copy(matrix, this._unjitteredProjection);
    if (this.taaJitterEnabled) {
      // Halton offset is in [0,1]; map to [-1,+1] pixel range and convert to
      // NDC by dividing by viewport size (NDC spans 2 units across `width` px).
      matrix[8] += (2 * this._jitterOffset[0] - 1) / this.dimensions.width;
      matrix[9] += (2 * this._jitterOffset[1] - 1) / this.dimensions.height;
    }
  }

  /**
   * Snapshot last frame's matrices and advance the TAA jitter index. Subclasses
   * must call this at the START of their `prepareFrame()` — before any of their
   * own modelView/projection invalidations — so the snapshot reflects last
   * frame's state.
   */
  protected prepareTemporalFrame(): void {
    mat4.copy(this._previousViewProjection, this._unjitteredViewProjection);

    const cam = this.cameraPosition;
    const cx = cam[0] as number;
    const cy = cam[1] as number;
    const cz = cam[2] as number;
    if (this._lastCameraPositionValid) {
      const dx = cx - this._lastCameraPosition[0];
      const dy = cy - this._lastCameraPosition[1];
      const dz = cz - this._lastCameraPosition[2];
      if (dx * dx + dy * dy + dz * dz > TELEPORT_THRESHOLD_SQR) {
        this.invalidateHistory();
      }
    }
    this._lastCameraPosition[0] = cx;
    this._lastCameraPosition[1] = cy;
    this._lastCameraPosition[2] = cz;
    this._lastCameraPositionValid = true;

    if (this.taaJitterEnabled) {
      this._haltonIndex = (this._haltonIndex + 1) % 16;
      this._jitterOffset[0] = halton(this._haltonIndex + 1, 2);
      this._jitterOffset[1] = halton(this._haltonIndex + 1, 3);
      this.invalidatedProjection = true;
    }
  }

  public override invalidateHistory(): void {
    super.invalidateHistory();
    this._lastCameraPositionValid = false;
  }

  public invalidateProjection(): void {
    this.invalidatedProjection = true;
  }

  public get frustum(): ViewFrustum {
    // force refresh if required
    this.modelViewMatrix;
    this.projectionMatrix;
    return this._viewFrustum;
  }

  protected abstract applyModelView(matrix: mat4): void;

  public abstract override get targetPosition(): vec3 | number[];

  public abstract override get cameraPosition(): vec3 | number[];

  public abstract override prepareFrame(): void;
}
